import { compact } from 'lodash';

import {
    BANNER_PREVIEW_SERP_TITLE_PX_LIMIT,
    TITLE_SENTENCE_SEPARATOR_FOR_BANNER_PREVIEW_DASH,
    TITLE_SENTENCE_SEPARATOR_FOR_BANNER_PREVIEW_SLASH
} from '../../constants';

import { getBannerPreviewTitleLength } from './getBannerPreviewTitleLength';

export interface Substitution {
    title: string;
    body: string;
}

export interface IgnoredIndex {
    start: number;
    end?: number;
}

class BannerPreviewHelpers {
    private RUS_LETTERS = '\u0430-\u043F\u0440-\u044F\u0410-\u042F\u0451\u0401';
    private RUS_LAT_LETTERS = 'a-zA-Z' + this.RUS_LETTERS;
    private RUS_LAT_LETTERS_AND_DIGITS = this.RUS_LAT_LETTERS + '\\d';
    private TITLE_LENGTH_LIMIT = 56;
    private RE_SIGNS = /[.!?…]/g;
    private RE_TO_SPLIT_SENTENCES = /([…!?]+\s*|\.{3}\s*|\.+\s+|\.+(?=[A-ZА-ЯЁҐЄІЇÇĞİÖŞÜ]))/;
    // END_SENTENCE_EXCLUSION_REGEXP используется для определения исключений в следующих выражениях
    // текст600руб. || 600руб. || 600 руб. согласно правилу 7 и правилу 22 в требованиях
    // https://wiki.yandex-team.ru/users/nadiano/movingtext/
    /* tslint:disable */
    private END_SENTENCE_EXCLUSION = [
        'им',
        'с',
        'яндекс',
        'и',
        'тыс',
        'млн',
        'абон',
        'м',
        'юр',
        'г',
        'руб',
        'р',
        'ак',
        '₽'
    ];
    /* tslint:enable */
    private END_SENTENCE_EXCLUSION_REGEXP = new RegExp(
        '^(.*[0-9]|.*\\s)*(' + this.END_SENTENCE_EXCLUSION.join('|') + ')$',
        'i'
    );
    private PAIR_DIFFERENT_SIGN = [
        ['(', ')'],
        ['{', '}'],
        ['<', '>'],
        ['«', '»']
    ];
    private PAIR_SAME_SIGN = ['"', '¨', "'"];
    private REG_EXP_ESCAPE_REG = /[-\/\\^$*+?.()|\[\]{}]/g;
    private bracketRegExp: RegExp;
    private apostropheRegexp: RegExp;
    private bracketsHelperObject: object;

    public getTitleAnBodyForPreview = (
        isMobile: boolean,
        title: string = '',
        titleExtension: string = '',
        body: string = '',
        domain: string = ''
    ): Substitution => {
        const fullTitle = compact([title, titleExtension]).join(
            TITLE_SENTENCE_SEPARATOR_FOR_BANNER_PREVIEW_DASH
        );

        const isLongTitle =
            getBannerPreviewTitleLength(fullTitle) >
            BANNER_PREVIEW_SERP_TITLE_PX_LIMIT;
        const isSubstituation =
            (isLongTitle && !isMobile) || titleExtension.length === 0;

        if (isSubstituation) {
            return SerpPreviewHelpers.serpTitleSubstitution(
                isLongTitle ? title : fullTitle,
                body,
                domain
            );
        }

        return {
            title: fullTitle.trim(),
            body: body.trim()
        };
    };

    // Подставляет первое предложение из описания баннера в заголовок, есть ряд исключений, смотреть DIRECT-42716
    private serpTitleSubstitution = (
        title: string = '',
        body: string = '',
        domain: string = ''
    ): Substitution => {
        title = title.trim();
        body = body.trim();
        domain = domain.trim();

        const ignoredIndexes = this.getIgnoredIndexesByBrackets(body);

        // если нарушена сбалансированность скобок/кавычек то просто добавляем домен
        if (!ignoredIndexes) {
            return this.getTitleAndBodyWithoutSubstitution(title, body, domain);
        }

        // разбиваем описание на фразы по знакам препинания
        // пример, 'Большой выбор экскурсий... От 400 руб/чел. Бесплатно… Каждый день' ->
        // Array [ "Большой выбор экскурсий", "... ", "От 400 руб/чел", ". ", "Бесплатно", "…", " Каждый день" ]
        const splitedBody = body
            .split(this.RE_TO_SPLIT_SENTENCES)
            .filter(a => a);
        // получаем первое предложение с учетом исключений
        let sentence = this.getFirstSentenceFromSplittedBody(
            splitedBody,
            ignoredIndexes
        );
        // остаток описания, без выбранной фразы
        const remnantBody = (
            body.split(new RegExp('^' + this.regExp(sentence), 'ig'))[1] || ''
        ).trim();
        const titleLowerCaseWithoutSigns = title
            .toLowerCase()
            .replace(this.RE_SIGNS, '');
        const phraseLowerCase = sentence
            .toLowerCase()
            .replace(this.RE_SIGNS, '');

        // Если в конце фразы для подстановки точка (и это не часть троеточия), то ее нужно обрезать DIRECT-58951
        if (!sentence.match(/[\.]{3}$/)) {
            sentence = sentence.replace(/\.$/, '');
        }

        // Ширина заголовка после подстановки не должна привышать TITLE_PX_LIMIT
        // Если текст содержит только одну фразу, то перестановки не происходит.
        // При вхождении первого предложения в заголовок или заголовка в первое предложение текста без учета регистра, перестановки не происходит
        if (
            !remnantBody.length ||
            getBannerPreviewTitleLength(
                title +
                    TITLE_SENTENCE_SEPARATOR_FOR_BANNER_PREVIEW_DASH +
                    sentence
            ) > BANNER_PREVIEW_SERP_TITLE_PX_LIMIT ||
            titleLowerCaseWithoutSigns.indexOf(phraseLowerCase) !== -1 ||
            phraseLowerCase.indexOf(titleLowerCaseWithoutSigns) !== -1
        ) {
            return this.getTitleAndBodyWithoutSubstitution(title, body, domain);
        }

        return {
            title:
                title +
                TITLE_SENTENCE_SEPARATOR_FOR_BANNER_PREVIEW_DASH +
                sentence,
            body: remnantBody
        };
    };

    private isBracket = (str: string): boolean => {
        if (!this.bracketRegExp) {
            this.bracketRegExp = new RegExp(
                this.PAIR_DIFFERENT_SIGN.reduce((result, item) => {
                    return result.concat(item);
                }, this.PAIR_SAME_SIGN)
                    .map(regExpString => {
                        return regExpString.replace(
                            this.REG_EXP_ESCAPE_REG,
                            '\\$&'
                        );
                    })
                    .join('|')
            );
        }

        return this.bracketRegExp.test(str);
    };

    private isApostrophe = (str: string): boolean => {
        if (!this.apostropheRegexp) {
            this.apostropheRegexp = new RegExp(
                '[' +
                    this.RUS_LAT_LETTERS_AND_DIGITS +
                    "]'[" +
                    this.RUS_LAT_LETTERS_AND_DIGITS +
                    ']',
                'i'
            );
        }

        return this.apostropheRegexp.test(str);
    };

    // Возвращает вспомогательный объект, для подсчета открывающихся и закрывающихся скобок/кавычек
    private getBracketsHelperObject = (): object => {
        if (!this.bracketsHelperObject) {
            this.bracketsHelperObject = {};

            // { '>': '<', '}': '{', ')': '(' },
            this.bracketsHelperObject = this.PAIR_DIFFERENT_SIGN.reduce(
                (res, item) => {
                    res[item[1]] = item[0];

                    return res;
                },
                this.bracketsHelperObject
            );

            // { ": null, ': null }
            this.bracketsHelperObject = this.PAIR_SAME_SIGN.reduce(
                (res, item) => {
                    res[item] = null;

                    return res;
                },
                this.bracketsHelperObject
            );
        }

        return this.bracketsHelperObject;
    };

    // Возвращает список индексов, между которыми нужно игнорирвать разделитель предложений
    // возвращает массив объектов, `s`, в которых - индекс с которого нужно начинать игнорировать разделитель,
    // `e` - индекс с которого нужно перестать игнорировать. Если возвращает `false` - значить нарушен баланс
    // скобок/кавычек, пустой массив - значит скобок/кавычек нет
    private getIgnoredIndexesByBrackets = (
        str: string
    ): Array<IgnoredIndex> | false => {
        if (!this.isBracket(str)) {
            return [];
        }

        const bracketsHelperObject = this.getBracketsHelperObject();
        const openBrackets: Array<string> = [];
        const ignoredMap: Array<IgnoredIndex> = [];
        const strLength = str.length;
        let isClean = true;
        let symbol;
        let i;
        let ignoredMapIndex = 0;
        let countOpenBrackets = 0;

        // Увеличивает счетчик открытых скобок/кавычек, заполняет список игнорируемых индексов
        const incrementOpenBracket = (index: number): void => {
            if (!countOpenBrackets) {
                ignoredMap[ignoredMapIndex] = { start: index };
            }
            countOpenBrackets++;
        };

        // Уменьшает счетчик открытых скобок/кавычек, заполняет список игнорируемых индексов
        const decrementOpenBracket = (index: number): void => {
            countOpenBrackets--;
            if (!countOpenBrackets) {
                ignoredMap[ignoredMapIndex].end = index;
                ignoredMapIndex++;
            }
        };

        const qtyQuote = Array.prototype.reduce.call(
            str,
            (sum: number, char: string) => {
                return char === "'" ? ++sum : sum;
            },
            0
        );

        // проверяем на сбалансированность
        for (i = 0; isClean && i < strLength; i++) {
            symbol = str[i];

            // если символ скобка/кавычка, но не апостроф
            if (
                this.isBracket(symbol) &&
                (symbol === "'"
                    ? qtyQuote !== 1 &&
                      !this.isApostrophe(
                          (str[i - 1] || '') + symbol + (str[i + 1] || '')
                      )
                    : true)
            ) {
                // открывающий и закрывающий символы одинаковые
                if (bracketsHelperObject[symbol] === null) {
                    if (openBrackets[openBrackets.length - 1] === symbol) {
                        openBrackets.pop();
                        decrementOpenBracket(i);
                    } else {
                        openBrackets.push(symbol);
                        incrementOpenBracket(i);
                    }
                    // открывающий и закрывающий символы разные
                } else {
                    if (bracketsHelperObject[symbol]) {
                        isClean =
                            openBrackets.pop() === bracketsHelperObject[symbol];
                        decrementOpenBracket(i);
                    } else {
                        openBrackets.push(symbol);
                        incrementOpenBracket(i);
                    }
                }
            }
        }

        return isClean && !openBrackets.length && ignoredMap;
    };

    // Удаляет www из имени домена
    private stripWww = (domain: string): string => {
        let trimDomain = domain.trim().toLowerCase();

        if (
            trimDomain.match(/^www\..*\./) &&
            !trimDomain.match(
                '^www\\.((pp|ac|boom|msk|spb|nnov).ru|(net|org|com|int|edu).[-a-z]+)$'
            )
        ) {
            trimDomain = trimDomain.replace(/^www\./, '');
        }
        return trimDomain;
    };

    // Подставляет домен в заголовок
    // Если полученная строка не привышает TITLE_LENGTH_LIMIT - возвращаем результат
    // Иначе возвращается исходный заголовок
    private getTitleAndBodyWithoutSubstitution = (
        title: string,
        body: string,
        domain: string
    ): Substitution => {
        const titleWithDomain =
            title +
            (domain
                ? TITLE_SENTENCE_SEPARATOR_FOR_BANNER_PREVIEW_SLASH +
                  this.stripWww(domain)
                : '');

        return {
            title:
                titleWithDomain.length > this.TITLE_LENGTH_LIMIT
                    ? title
                    : titleWithDomain,
            body: body
        };
    };

    private regExp = (regExpString: string): string => {
        return regExpString.replace(this.REG_EXP_ESCAPE_REG, '\\$&');
    };

    // Вытаскивает из, разбитого по знаками препинания, массива описания фразу
    // Возращает фразу, которую вытащили (с учетом исключений) для подстановки в заголовок
    private getFirstSentenceFromSplittedBody = (
        splitedBody: Array<string>,
        ignoredIndexes: Array<IgnoredIndex>,
        prevLength: number = 0
    ): string => {
        // пример, splitedBody ==
        // Array [ "Большой выбор экскурсий", "... ", "От 400 руб/чел", ". ", "Бесплатно", "…", " Каждый день" ]
        if (splitedBody.length < 1) {
            return '';
        }

        // Вынимаем текст и знак препинания (по которому этот кусок отделился)
        const text = splitedBody.shift();
        const punctuation = splitedBody.shift() || '';
        let phrase = text + punctuation;
        const indexPunctuationInWholePhrase =
            prevLength + (text ? text.length : 0);
        const ignoreThisPunctuation = ignoredIndexes.some(
            (ignoredIndex: IgnoredIndex) => {
                return Boolean(
                    ignoredIndex.start < indexPunctuationInWholePhrase &&
                        (ignoredIndex.end &&
                            ignoredIndex.end > indexPunctuationInWholePhrase)
                );
            }
        );

        // Если в первом предложении есть сокращения-исключения (возможно, с числом в начале без пробела),
        // то ищется следующий знак окончания предложения.
        if (
            ignoreThisPunctuation ||
            (punctuation.trim() === '.' &&
                this.END_SENTENCE_EXCLUSION_REGEXP.test(text || ''))
        ) {
            phrase += this.getFirstSentenceFromSplittedBody(
                splitedBody,
                ignoredIndexes,
                prevLength + phrase.length
            );
        }

        return phrase.trim();
    };
}

export const SerpPreviewHelpers = new BannerPreviewHelpers();
